/**
 * How this bundle behaves when a host page injects it instead of serving it
 * standalone (the SendHub webclient / React SPA fetch our index.html and append
 * the bundle's scripts to their own page).
 *
 * A host opts in by setting `window.__SH_INTEGRATIONS_EMBED__` BEFORE it appends
 * the scripts. With nothing set, everything is as before: mount on `#root` and
 * route on the browser URL. The Backbone webclient relies on that default (it
 * creates its own `#root` and shares the URL with this app), so changing a
 * default here changes that integration.
 */
export interface EmbedConfig {
  /**
   * Id of the element to mount on. Needed when the host page already owns
   * `#root` (the React SPA does) — mounting there replaces the whole host.
   */
  rootId?: string
  /**
   * `memory` keeps routing inside the bundle and leaves the browser URL to the
   * host, so the two routers cannot fight over history. `browser` (default)
   * routes on the real URL.
   */
  routerMode?: 'browser' | 'memory'
  /**
   * Origin our bundle and API are served from (e.g. https://integrations.sendhub.com).
   * A host knows this exactly; guessing it from the page's <script> tags is
   * ambiguous when the host has its own `main.*` entry script.
   */
  origin?: string
}

declare global {
  interface Window {
    __SH_INTEGRATIONS_EMBED__?: EmbedConfig
  }
}

export interface ResolvedEmbedConfig {
  origin: string | undefined
  rootId: string
  routerMode: 'browser' | 'memory'
  /** True when a host asked for any embed behaviour. */
  embedded: boolean
}

export function getEmbedConfig(source: Window | undefined = typeof window === 'undefined' ? undefined : window): ResolvedEmbedConfig {
  const requested = source?.__SH_INTEGRATIONS_EMBED__
  return {
    origin: requested?.origin ? requested.origin.replace(/\/+$/, '') : undefined,
    rootId: requested?.rootId || 'root',
    routerMode: requested?.routerMode === 'memory' ? 'memory' : 'browser',
    embedded: !!requested,
  }
}
